import { inject, Injectable } from '@angular/core';
import { NotificationService } from '../../app/services/notification.service';
import { PublicExperienceStateModel } from './public-experience.model';
import { Action, State, StateContext } from '@ngxs/store';
import { PublicExperienceService } from './public-experience.service';
import {
  ClearNewPublicExperience,
  ClearPublicExperience,
  CreatePublicExperience,
  DeletePublicExperience,
  DuplicatePublicExperience,
  GetAllDefaultModules,
  GetAllPublicExperiences,
  GetPublicExperience,
  GetPublicExperienceList,
  UpdatePublicExperience,
} from './public-experience.actions';
import { catchError, tap, throwError } from 'rxjs';
import { ObjectToUrlParams } from '../../app/services/global.service';
import { GetUser } from '../users-store/user.actions';

@State<PublicExperienceStateModel>({
  name: 'PublicExperience',
  defaults: {
    defaultModules: [],
    isFooterEditable: true,
    isProcessing: true,
    isItemLoading: false,
    headers: [],
    pagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
    },
    publicExperiences: undefined,
    allPublicExperiences: [],
    publicExperience: null,
    newPublicExperience: null,
    error: null,
  },
})
@Injectable({
  providedIn: 'root',
})
export class PublicExperienceState {
  private readonly notificationService = inject(NotificationService);
  private readonly publicExperienceService = inject(PublicExperienceService);

  @Action(CreatePublicExperience)
  createPublicExperience(
    { dispatch, patchState }: StateContext<PublicExperienceStateModel>,
    action: CreatePublicExperience,
  ) {
    patchState({ isProcessing: true });
    return this.publicExperienceService.createPublicExperience(action.payload).pipe(
      tap(async (res) => {
        patchState({
          isProcessing: false,
          newPublicExperience: res.payload,
        });

        // Refresh user subscription info after creating public experience
        // This ensures the canAddPublicExperience limit is updated
        dispatch(new GetUser());

        dispatch(new GetPublicExperience(res.payload.id!));
      }),
      catchError(async (error) => {
        patchState({ isProcessing: false });
        return throwError(() => error);
      }),
    );
  }

  @Action(GetPublicExperienceList)
  getPublicExperienceList(
    { patchState }: StateContext<PublicExperienceStateModel>,
    action: GetPublicExperienceList,
  ) {
    patchState({
      isProcessing: true,
      error: null,
      publicExperiences: undefined,
    });
    return this.publicExperienceService.getPublicExperiences(action.payload).pipe(
      tap(async (res) => {
        patchState({
          publicExperiences: res.payload,
          pagination: res.pagination,
          headers: res.headers,
          isProcessing: false,
          error: null,
        });
      }),
      catchError(async (error) => {
        const errorMessage = error.message || 'Failed to fetch public experiences';
        patchState({
          isProcessing: false,
          error: errorMessage,
        });
        this.notificationService.openErrorToast(errorMessage);
        return throwError(() => error);
      }),
    );
  }

  @Action(GetAllPublicExperiences)
  getAllPublicExperiences({ patchState }: StateContext<PublicExperienceStateModel>) {
    patchState({ isProcessing: true, publicExperiences: undefined });
    return this.publicExperienceService
      .getPublicExperiences({
        first: 0,
        rows: 10000,
        sortBy: 'templateName',
        sortOrder: 'ASC',
        filters: ObjectToUrlParams({}),
        search: '',
      })
      .pipe(
        tap(async (res) => {
          patchState({
            allPublicExperiences: res.payload,
            isProcessing: false,
          });
        }),
      );
  }

  @Action(GetPublicExperience)
  getPublicExperience(
    { patchState }: StateContext<PublicExperienceStateModel>,
    action: GetPublicExperience,
  ) {
    patchState({ isItemLoading: true, isProcessing: true });
    return this.publicExperienceService.getPublicExperience(action.id).pipe(
      tap(async (res) => {
        patchState({
          publicExperience: res.payload,
          isItemLoading: false,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isItemLoading: false, isProcessing: false });
      }),
    );
  }

  @Action(DeletePublicExperience)
  deletePublicExperience(
    { dispatch, patchState }: StateContext<PublicExperienceStateModel>,
    action: DeletePublicExperience,
  ) {
    patchState({ isProcessing: true });
    return this.publicExperienceService.deletePublicExperience(action.id).pipe(
      tap(async (_res) => {
        patchState({
          isProcessing: false,
        });

        // Show success notification
        this.notificationService.openSuccessToast('Public experience deleted successfully!');

        // Refresh user subscription info after deleting public experience
        // This ensures the canAddPublicExperience limit is updated
        dispatch(new GetUser());
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
        return throwError(() => _error);
      }),
    );
  }

  @Action(UpdatePublicExperience)
  updatePublicExperience(
    { dispatch, patchState }: StateContext<PublicExperienceStateModel>,
    action: UpdatePublicExperience,
  ) {
    patchState({ isProcessing: true });
    return this.publicExperienceService.updatePublicExperience(action.payload, action.id).pipe(
      tap(async (res) => {
        patchState({
          isProcessing: false,
        });

        // Show success notification
        this.notificationService.openSuccessToast('Public experience updated successfully!');

        dispatch(new GetPublicExperience(res.payload.id!));
      }),
      catchError(async (error) => {
        patchState({ isProcessing: false });
        return throwError(() => error);
      }),
    );
  }

  @Action(DuplicatePublicExperience)
  duplicatePublicExperience(
    { dispatch, patchState }: StateContext<PublicExperienceStateModel>,
    action: DuplicatePublicExperience,
  ) {
    patchState({ isProcessing: true });
    return this.publicExperienceService.duplicatePublicExperience(action.id).pipe(
      tap(async (res) => {
        patchState({
          isProcessing: false,
        });

        // Show success notification
        this.notificationService.openSuccessToast('Public experience duplicated successfully!');

        // Refresh user subscription info after duplicating public experience
        // This ensures the canAddPublicExperience limit is updated
        dispatch(new GetUser());
      }),
      catchError(async (error) => {
        patchState({ isProcessing: false });
        return throwError(() => error);
      }),
    );
  }

  @Action(ClearPublicExperience)
  clearPublicExperience({ patchState }: StateContext<PublicExperienceStateModel>) {
    patchState({ publicExperience: null });
  }

  @Action(ClearNewPublicExperience)
  clearNewPublicExperience({ patchState }: StateContext<PublicExperienceStateModel>) {
    patchState({ newPublicExperience: null });
  }

  @Action(GetAllDefaultModules)
  getAllDefaultModules(
    { patchState }: StateContext<PublicExperienceStateModel>,
    action: GetAllDefaultModules,
  ) {
    patchState({ isProcessing: true });
    return this.publicExperienceService.getAllDefaultModules().pipe(
      tap(async (res) => {
        patchState({
          defaultModules: res.payload.modules,
          isFooterEditable: res.payload.isFooterEditable,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }
}
